/**
 * MapLayerControls — Layer selection drawer for the map.
 *
 * Primary layers: single-select (For You, Nearby, All Activities)
 * Secondary overlays: multi-select (Commons, Groups, Institutions, Businesses*, Sponsors*)
 * * = scaffolding only, not fully implemented
 *
 * Design: calm, minimal — not overbuilt. The map should answer
 * "What is happening around me?" not "What features does this map have?"
 */

import React from 'react';
import { Card } from '../ui/card';
import {
  User, MapPin, Activity, Landmark, Users, Building, Briefcase, Star, X
} from 'lucide-react';

const PRIMARY_LAYERS = [
  { id: 'nearby', label: 'Nearby', icon: MapPin, description: 'Everything in your area' },
  { id: 'all_activities', label: 'All Activities', icon: Activity, description: 'Confirmed & active' },
];

const SECONDARY_OVERLAYS = [
  { id: 'commons', label: 'Commons', icon: Landmark, color: '#7FA5B8' },
  { id: 'groups', label: 'Public Groups', icon: Users, color: '#E8A87C' },
  { id: 'institutions', label: 'Institutions', icon: Building, color: '#7B2D3B' },
  { id: 'businesses', label: 'Businesses', icon: Briefcase, color: '#3B6FA0' },
  { id: 'sponsors', label: 'Sponsors', icon: Star, color: '#C9A227' },
];

export default function MapLayerControls({
  activeLayer,
  activeOverlays,
  onLayerChange,
  onOverlayToggle,
  onClose,
}) {
  return (
    <div className="absolute top-16 right-3 z-[1000] w-64" data-testid="map-layer-controls">
      <Card
        className="shadow-xl overflow-hidden"
        style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--fy-text-disabled)' }}>
            Layers
          </span>
          <button onClick={onClose} className="p-1" data-testid="layers-close-btn">
            <X className="w-4 h-4" style={{ color: 'var(--fy-text-disabled)' }} />
          </button>
        </div>

        {/* Primary layers — single select */}
        <div className="px-3 pb-2 space-y-1">
          {PRIMARY_LAYERS.map(layer => {
            const Icon = layer.icon;
            const isActive = activeLayer === layer.id;
            return (
              <button
                key={layer.id}
                onClick={() => onLayerChange(layer.id)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all"
                style={{
                  backgroundColor: isActive ? 'var(--lk-sage-muted, #EDF1E8)' : 'transparent',
                }}
                data-testid={`layer-${layer.id}`}
              >
                <Icon
                  className="w-4 h-4 flex-shrink-0"
                  style={{ color: isActive ? 'var(--lk-sage)' : 'var(--fy-text-disabled)' }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: isActive ? 'var(--lk-sage)' : 'var(--fy-text-primary)' }}>
                    {layer.label}
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--fy-text-disabled)' }}>
                    {layer.description}
                  </p>
                </div>
                {isActive && (
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--lk-sage)' }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Divider */}
        <div className="mx-4 my-1" style={{ borderTop: '1px solid var(--fy-border-subtle)' }} />

        {/* Secondary overlays — multi select */}
        <div className="px-3 pt-1 pb-3 space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide px-3 mb-1" style={{ color: 'var(--fy-text-disabled)' }}>
            Overlays
          </p>
          {SECONDARY_OVERLAYS.map(overlay => {
            const Icon = overlay.icon;
            const isActive = activeOverlays.includes(overlay.id);
            const isScaffold = overlay.scaffold;
            const pinColor = overlay.color;
            return (
              <button
                key={overlay.id}
                onClick={() => !isScaffold && onOverlayToggle(overlay.id)}
                className="w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-left transition-all"
                style={{
                  backgroundColor: isActive ? `${pinColor}12` : 'transparent',
                  opacity: isScaffold ? 0.4 : 1,
                  cursor: isScaffold ? 'default' : 'pointer',
                }}
                disabled={isScaffold}
                data-testid={`overlay-${overlay.id}`}
              >
                <div
                  className="w-5 h-5 rounded flex items-center justify-center border"
                  style={{
                    borderColor: isActive ? pinColor : 'var(--fy-border-subtle)',
                    backgroundColor: isActive ? pinColor : 'transparent',
                  }}
                >
                  {isActive && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" className="w-3 h-3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: pinColor }} />
                <span className="text-sm" style={{ color: isActive ? pinColor : 'var(--fy-text-primary)', fontWeight: isActive ? 600 : 400 }}>
                  {overlay.label}
                  {isScaffold && <span className="text-[10px] ml-1" style={{ color: 'var(--fy-text-disabled)' }}>soon</span>}
                </span>
              </button>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
